import { useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi, type Tenant } from '../../api/tenants';

export function TenantSearchPage() {
  const [query, setQuery] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [pan, setPan] = useState('');
  const [country, setCountry] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [status, setStatus] = useState('');
  const [results, setResults] = useState<Tenant[]>([]);
  const [count, setCount] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function search() {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (query.trim()) params.q = query.trim();
      if (gstNumber.trim()) params.gst_number = gstNumber.trim();
      if (pan.trim()) params.pan = pan.trim();
      if (country.trim()) params.country = country.trim();
      if (businessType.trim()) params.business_type = businessType.trim();
      if (status) params.status = status;
      const res = await tenantApi.search(params);
      setResults(res.results ?? []);
      setCount(res.count ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/tenants" className="text-sm text-slate-600 hover:underline">
        ← Tenants
      </Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Tenant Search</h1>
      <p className="mt-1 text-sm text-slate-600">Search by name, GST, PAN, country, or business type.</p>

      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <div className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-2">
        <input placeholder="Query" value={query} onChange={(e) => setQuery(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input placeholder="GST number" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input placeholder="PAN" value={pan} onChange={(e) => setPan(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input placeholder="Business type" value={businessType} onChange={(e) => setBusinessType(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2">
          <option value="">Any status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
        </select>
        <button type="button" onClick={() => void search()} className="rounded-lg bg-slate-900 px-4 py-2 text-white md:col-span-2 md:w-fit">
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      <p className="mt-4 text-sm text-slate-500">{count} results</p>
      <ul className="mt-4 space-y-3">
        {results.map((t) => (
          <li key={t.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <Link to={`/tenants/${t.id}`} className="font-medium text-slate-900 hover:underline">
                  {t.name}
                </Link>
                <p className="text-sm text-slate-600">{t.slug}</p>
                {t.business?.legal_name && <p className="text-sm text-slate-500">{t.business.legal_name}</p>}
              </div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs capitalize text-slate-700">{t.status}</span>
            </div>
            <Link to={`/tenants/${t.id}/dashboard`} className="mt-2 inline-block text-xs text-slate-600 hover:underline">
              Dashboard
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
